import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import * as Promise from 'bluebird';
import { Component } from 'react';
import {
  ColumnDefinition,
  getFilters,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter,
  getPagesCount,
  isFilterExist,
  isSortExist,
  TABLE_SORT_DIRECTION,
} from 'Src/helpers/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Loader } from 'Src/components/Loader/Loader';
import { Grid } from 'Src/components/Grid/Grid';
import {
  addPackage,
  deletePackage,
  getAllPackages,
  getPackages,
  getPackagesCount,
  updatePackage,
} from 'Src/services/superadmin/packages';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { PackageForm } from './PackageForm/PackageForm';
import { Package } from 'Src/models/packages';
import { getPackageProducts } from '../../../../../../helpers/accessor';
import { ResizablePanel } from '../../../../../../components/ResizablePanel/ResizablePanel';
import { PACKAGE_TABS } from '../../../../../../consts/package';
import { TABS } from '../../../../../../consts/common';
import { convertToFilterWithPlus } from '../../../../../../helpers/table';
import { GenericSummary } from '../../../../../../components/GenericSummary/GenericSummary';
import { PackageSummary } from './PackageSummary/PackageSummary';
import { Button } from '../../../../../../components/Button/Button';
import { getAllPriceSets } from '../../../../../../services/superadmin/priceSets';
import { PackagePriceSetsSummary } from './PackagePriceSetsSummary/PackagePriceSetsSummary';
import * as propz from 'propz';
import { LIMIT } from '../../../../../../consts/table';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Name',
    field: 'name',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['name'],
  },
  {
    text: 'Description',
    field: 'description',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['description'],
  },
  {
    text: 'Products',
    field: 'products',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getPackageProducts,
  },
];

interface State {
  items: Package[];
  currentPage: number;
  selectedItems: Package[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;
  isPackageFormModalOpen: boolean;
  currentTabIndex: number;
  priceSets: any[];
  isConfirmationModalOpen: boolean;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class Packages extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      isPackageFormModalOpen: false,
      currentTabIndex: 0,
      priceSets: [],
      isConfirmationModalOpen: false,
    };
  }

  componentDidMount() {
    this.setState({
      isLoading: true,
    });

    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true,
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user, location } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters(COLUMNS, search);

    const isShowFilter = isFilterExist(filters);
    const isDataFiltered = isFilterExist(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter(page, where, order);

    const getItemsPromise = getPackages(user, serverQueryFilter);
    const getItemsCountPromise = getPackagesCount(user, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return Promise.all(promises).then(([countObj, items]) => {
      const packageId = propz.get(location, ['state', 'packageId']);

      if (typeof packageId !== 'undefined') {
        const packageItem = items.find(item => item.id === packageId);
        this.setState({
          currentPage: page,
          sortDirection: sortDirection as TABLE_SORT_DIRECTION,
          sortColumnsName: sortColumnsName,
          isShowFilter: isShowFilter,
          isDataFiltered: isDataFiltered,
          filters: filters,
          items: items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          selectedItems: typeof packageItem !== 'undefined' ? [packageItem] : [],
        });

        //clear history
        const state = { ...(history.location.state as any) };
        delete state.packageId;
        history.replace({ ...history.location, state });
      } else {
        this.setState({
          currentPage: page,
          sortDirection: sortDirection as TABLE_SORT_DIRECTION,
          sortColumnsName: sortColumnsName,
          isShowFilter: isShowFilter,
          isDataFiltered: isDataFiltered,
          filters: filters,
          items: items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
        });
      }

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems, currentTabIndex } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
      currentTabIndex: 0,
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const selectedItemsNext = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: selectedItemsNext,
      isSelectAllChecked: true,
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true,
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    getAllPackages(user, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const selectedItemsNext = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: selectedItemsNext,
        isSelectAllChecked: true,
        isLoading: false,
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue,
            },
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue,
            },
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value,
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue,
        };
      }
    }

    this.setState({
      filters: nextFilters,
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    const filtersConverted = convertToFilterWithPlus(filters);

    if (isFilter) {
      search.push(getSearchFilter(filtersConverted));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&'),
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: '',
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter,
    });
  };

  renderFormModal(): React.ReactNode {
    const { isPackageFormModalOpen, selectedItems } = this.state;
    const { user } = this.props;

    if (selectedItems.length === 1) {
      const packageItem = selectedItems[0];
      return (
        <SimpleModal isOpen={isPackageFormModalOpen}>
          <PackageForm
            packageItem={packageItem}
            onCancel={this.onCancelButtonClick}
            onSubmit={this.onSubmitForm}
            user={user}
          />
        </SimpleModal>
      );
    } else {
      return (
        <SimpleModal isOpen={isPackageFormModalOpen}>
          <PackageForm onCancel={this.onCancelButtonClick} onSubmit={this.onSubmitForm} user={user} />
        </SimpleModal>
      );
    }
  }

  onSubmitForm = data => {
    const { selectedItems, total } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true,
    });

    const dataToTransfer = {
      name: data.name,
      description: data.description,
      // pic: data.pic,
      products: data.products.map(product => ({
        productId: product.id,
        name: product.name,
      })),
    };

    if (selectedItems.length === 1) {
      const packageItem = selectedItems[0];
      const { id } = packageItem;

      updatePackage(user, id, dataToTransfer).then(res => {
        this.setState({
          isPackageFormModalOpen: false,
          selectedItems: [res],
          isSelectAllChecked: false,
        });
        this.setItems();
      });
    } else {
      addPackage(user, dataToTransfer).then(res => {
        const lastPage = getPagesCount(total + 1, LIMIT);
        this.setState({
          isPackageFormModalOpen: false,
          selectedItems: [res],
          isSelectAllChecked: false,
        });
        this.setCurrentPageParams(lastPage);
        this.setItems();
      });
    }
  };

  onCancelButtonClick = () => {
    this.setState({
      isPackageFormModalOpen: false,
    });
  };

  onCreatePackageClick = () => {
    this.setState({
      isPackageFormModalOpen: true,
    });
  };

  onEditPackageClick = () => {
    this.setState({
      isPackageFormModalOpen: true,
    });
  };

  showConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: true,
    });
  };

  hideConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: false,
    });
  };

  renderConfirmationModal(): React.ReactNode {
    const { isConfirmationModalOpen, selectedItems } = this.state;

    return (
      <SimpleModal
        isOpen={isConfirmationModalOpen}
        title={'Confirmation'}
        body={`Are you sure you want to remove ${selectedItems.length === 1 ? 'package' : 'packages'}?`}
        buttonText={'Remove'}
        buttonCancelText={'Cancel'}
        onButtonClick={this.onDeletePackageClick}
        onCloseClick={this.hideConfirmationModal}
      />
    );
  }

  onDeletePackageClick = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true,
      isConfirmationModalOpen: false,
    });

    Promise.all(selectedItems.map(selectedItem => deletePackage(user, selectedItem.id))).then(res => {
      this.setState({
        selectedItems: [],
        isSelectAllChecked: false,
      });

      this.setItems();
    });
  };

  renderGrid(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
    } = this.state;

    const actionItems = [
      {
        itemText: 'Create package',
        onItemClick: this.onCreatePackageClick,
        isActive: selectedItems.length === 0,
      },
      {
        itemText: 'Edit package',
        onItemClick: this.onEditPackageClick,
        isActive: selectedItems.length === 1,
      },
      {
        itemText: selectedItems.length === 1 ? 'Delete package' : 'Delete packages',
        onItemClick: this.showConfirmationModal,
        isActive: selectedItems.length > 0,
      },
    ];

    const filterOptions = {};

    return (
      <Grid
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
      />
    );
  }

  getTabs(): React.ReactNode {
    const { currentTabIndex, selectedItems, priceSets } = this.state;

    const tabs = PACKAGE_TABS;
    const currentTab = tabs[currentTabIndex];

    switch (currentTab) {
      case TABS.SUMMARY:
        return <PackageSummary packageItem={selectedItems[0]} />;
      case TABS.PRICE_SETS:
        return <PackagePriceSetsSummary priceSets={priceSets} />;
    }
  }

  onTabClick = (event, index: number) => {
    event.preventDefault();
    const { user } = this.props;
    const { selectedItems } = this.state;
    const packageItem = selectedItems[0];
    const { id } = packageItem;

    const tabs = PACKAGE_TABS;
    const currentTab = tabs[index];

    switch (currentTab) {
      case TABS.PRICE_SETS:
        this.setState({
          isLoading: true,
        });
        getAllPriceSets(user, id).then(priceSets => {
          this.setState({
            currentTabIndex: index,
            priceSets: priceSets,
            isLoading: false,
          });
        });
        break;
      default:
        this.setState({
          currentTabIndex: index,
        });
    }
  };

  onEditPriceSetsButtonClick = () => {
    const { selectedItems } = this.state;
    const packageItem = selectedItems[0];
    const { id } = packageItem;

    const { history, location } = this.props;
    const { search } = location;
    const { basePath } = this.state;

    history.push({
      pathname: `${basePath}/priceSets`,
      search: `package=${id}`,
      state: { search: search },
    });
  };

  renderSummary(): React.ReactNode {
    const { selectedItems, currentTabIndex } = this.state;
    const isOneItemSelected = selectedItems.length === 1;

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select a package above</div>;
    }

    const tabs = this.getTabs();
    const currentTab = PACKAGE_TABS[currentTabIndex];

    const isPriceSetTab = currentTab === TABS.PRICE_SETS;

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={PACKAGE_TABS} currentTabIndex={currentTabIndex}>
        {isPriceSetTab && (
          <Button
            onClick={this.onEditPriceSetsButtonClick}
            text={'Edit price sets'}
            customClass={'btn btn-primary mt-3 mr-3 mb-3'}
          />
        )}
        {tabs}
      </GenericSummary>
    );
  }

  render() {
    const { isLoading, isPackageFormModalOpen, isConfirmationModalOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isPackageFormModalOpen || isConfirmationModalOpen ? 'mt-3 modal-open' : 'mt-3';

    return (
      <div className={classes}>
        {/*Place for render modal windows*/}
        {this.renderFormModal()}
        {this.renderConfirmationModal()}
        <div className="row">
          <div className="col-md-12">
            <ResizablePanel>
              {this.renderGrid()}
              {this.renderSummary()}
            </ResizablePanel>
          </div>
        </div>
      </div>
    );
  }
}
